import {
    CameraNotification,
    CameraNotificationStatus,
    CameraNotificationType,
} from '@EcamModel/model/CameraNotification';
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import color from '@theme/Colors';
import moment from 'moment';
import { ReactNode } from 'react';
import { useNotification } from '../hooks/NotificationProvider';
import EllipsisTypography from './EllipsisTypography';
import { BaseItem } from '@pages/cameras/list/styles';

export type NotificationItemProps = {
    item: CameraNotification;
    index: number;
    customNote?: ReactNode;
};

type Props = NotificationItemProps & {
    handleMarkRead: () => void;
    children: ReactNode;
    title: string;
};

export const CameraNotificationTypeMapping = {
    [CameraNotificationType.CameraThreshold]: {
        background: color.gold100,
        color: color.gold600,
        name: 'Warning',
    },
    [CameraNotificationType.Task]: {
        background: color.lightPrimary,
        color: color.success,
        name: 'Task',
    },
    [CameraNotificationType.PhotoOverload]: {
        background: '#FCE8E5',
        color: '#E01B00',
        name: 'Photo Overload',
    },
    [CameraNotificationType.PCNsLackingVrn]: {
        background: '#E5F2FF',
        color: color.secondary,
        name: 'PC Lacking Vrn',
    },
    [CameraNotificationType.GroupMatching]: {
        background: '#CC00661A',
        color: '#CC0066',
        name: 'PC Group Matching',
    },
    [CameraNotificationType.PTZPositionMismatch]: {
        background: '#FFEFE6',
        color: '#CC4E00',
        name: 'Missing Geozone Photo',
    },
    [CameraNotificationType.CameraDisconnected]: {
        background: '#F5F5F5',
        color: '#6B7280',
        name: 'Camera Disconnected',
    },
    [CameraNotificationType.LowCameraPhoto]: {
        background: '#FFF7E6',
        color: '#D97706',
        name: 'Low Camera Photo',
    },
    [CameraNotificationType.HighCameraPhoto]: {
        background: '#EDE9FE',
        color: '#6D28D9',
        name: 'High Camera Photo',
    },
    [CameraNotificationType.LowCameraBattery]: {
        background: '#FFE6EB',
        color: '#CC0033',
        name: 'Low Battery',
    },
};

export default function LayoutNotificationItem(props: Props) {
    const { markRead } = useNotification();
    const { handleMarkRead, item, children, index, title } = props;

    const isUnread = item?.Status === CameraNotificationStatus.Unread;
    const notificationMapping = CameraNotificationTypeMapping[item?.Type] ?? {
        background: color.grey200,
        color: color.grey600,
        name: 'Unknown',
    };

    return (
        <BaseItem
            key={index}
            component={Stack}
            sx={{
                display: 'flex',
                '&:hover': {
                    bgcolor: color.grey100,
                },
                cursor: 'pointer',
            }}
            onClick={handleMarkRead}
            gap={2}
        >
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{ opacity: isUnread ? 1 : 0.7 }}
            >
                <Stack direction={'row'} alignItems={'center'} gap={1} width={'100%'}>
                    {isUnread && (
                        <Tooltip title={'Mark as read'} placement="top">
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: 'red',
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    markRead([item?.Id!], true);
                                }}
                            />
                        </Tooltip>
                    )}
                    <Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            <EllipsisTypography title={title ?? item?.Camera?.Name ?? ''} />
                            <Box
                                bgcolor={notificationMapping.background}
                                p="2px 10px"
                                borderRadius={2}
                                maxHeight={'20px'}
                            >
                                <Typography
                                    variant="body2"
                                    textTransform={'capitalize'}
                                    color={notificationMapping.color}
                                >
                                    {notificationMapping.name}
                                </Typography>
                            </Box>
                        </Stack>
                        <Typography color={color.grey600}>{moment(item?.Created).fromNow()}</Typography>
                    </Stack>
                </Stack>
            </Stack>

            <Box sx={{ opacity: isUnread ? 1 : 0.7 }}>
                {item.Camera?.ParkingSpace?.Name && (
                    <Typography variant="caption" color={color.secondary}>
                        Parking: {item.Camera?.ParkingSpace?.Name}
                    </Typography>
                )}
                <Typography color="text.primary">{children || item.Notes}</Typography>
            </Box>
        </BaseItem>
    );
}
